import { useCallback, useEffect, useRef, useState } from 'react';
import { searchMindMaps } from '../../utils/mindmap/apiClient';

const EMPTY_RESULTS = Object.freeze([]);
const empty = (query, accountId) => ({ query, accountId, results: EMPTY_RESULTS, nextCursor: null, loading: false, error: '', incomplete: false, indexing: false });

export default function useGlobalMapSearch({ query, enabled, accountId }) {
  const [state, setState] = useState(() => empty('', accountId));
  const requestRef = useRef(null);
  const indexingRetriesRef = useRef(0);
  const fetchPages = useCallback(async (text, cursor = null, previous = []) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setState(current => ({ ...(current.query === text && current.accountId === accountId ? current : empty(text, accountId)), loading: true, error: '' }));
    try {
      let results = [...previous];
      let nextCursor = cursor;
      let incomplete = false;
      let indexing = false;
      // Continue through empty batches, with a visible continuation control
      // rather than an unbounded request chain for very large libraries.
      for (let page = 0; page < 4; page += 1) {
        const response = await searchMindMaps(text, { cursor: nextCursor, signal: controller.signal });
        results = [...results, ...(response.results || [])];
        nextCursor = response.nextCursor;
        incomplete ||= Boolean(response.unavailableDocuments || response.unavailableMaps);
        indexing ||= Boolean(response.indexing);
        if (requestRef.current !== controller || controller.signal.aborted) return;
        // Publish each page before requesting the next one. Titles/notes are
        // immediately usable while indexed document matches arrive afterward.
        setState({ query: text, accountId, results, nextCursor, loading: true, error: '', incomplete, indexing });
        if (!nextCursor || results.length - previous.length >= 20) break;
      }
      if (requestRef.current !== controller || controller.signal.aborted) return;
      setState(current => ({ query: text, accountId, results, nextCursor, loading: false, error: '',
        incomplete: incomplete || (current.query === text && current.incomplete), indexing }));
    } catch (error) {
      if (controller.signal.aborted || requestRef.current !== controller) return;
      setState(current => ({ ...current, loading: false,
        error: 'Could not search all maps. Current-map results are still available.' }));
    }
  }, [accountId]);

  useEffect(() => {
    requestRef.current?.abort();
    indexingRetriesRef.current = 0;
    setState(empty(query, accountId));
    if (!enabled || query.trim().length < 2 || query.trim().length > 200) return undefined;
    const timer = setTimeout(() => fetchPages(query), 300);
    return () => { clearTimeout(timer); requestRef.current?.abort(); };
  }, [query, enabled, accountId, fetchPages]);

  useEffect(() => {
    if (!enabled || state.query !== query || state.accountId !== accountId
      || state.loading || state.error || !state.indexing || indexingRetriesRef.current >= 5) return undefined;
    const timer = setTimeout(() => {
      indexingRetriesRef.current += 1;
      fetchPages(query);
    }, 2000);
    return () => clearTimeout(timer);
  }, [enabled, query, accountId, state.query, state.accountId, state.loading, state.error, state.indexing, fetchPages]);

  const loadMore = useCallback(() => {
    if (state.nextCursor && !state.loading && state.query === query) {
      fetchPages(query, state.nextCursor, state.results);
    }
  }, [fetchPages, query, state]);
  return { ...(enabled && state.query === query && state.accountId === accountId ? state : empty(query, accountId)), loadMore };
}
